import React, { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { DropdownInput } from '../dropdown/DropdownInput.old';
import { convertOldHelpTextProps } from '../text/converter.old';
import type { OldHelpTextProps } from '../text/converter.old';
import type { IconProps } from '../icons/generated';
import { Input } from '../input/Input.old';
import { NumericInput } from '../input/NumericInput.old';
import type { InputStatus } from '../input/types';
import type { CSSValueWithLength } from '../../styles';
import type { FormFieldHelperTextProps, FormFieldProps as FormFieldNewProps } from './FormField';
import type { FormHelperTextProps as FormHelperTextNewProps } from './FormHelperText';
import type { FormLabelProps as FormLabelNewProps } from './FormLabel';

export interface OldFormHelperTextProps extends Omit<FormHelperTextNewProps, 'leftIcon'> {
  /** @deprecated leftIcon을 대신 사용해주세요. */
  icon?: React.ReactElement<IconProps> | boolean;
  /** @deprecated FormHelperText 사용처에서 margin 마이그레이션 이후 스펙아웃될 예정입니다. */
  mt?: CSSValueWithLength;
}

type FormHelperTextStyleProps = { style?: React.CSSProperties };

export function convertOldFormHelperTextProps(
  props: OldFormHelperTextProps & FormHelperTextStyleProps,
): FormHelperTextNewProps & FormHelperTextStyleProps {
  const { icon, mt, style, ...restProps } = props;

  return {
    ...restProps,
    leftIcon: icon,
    style: mt !== undefined ? { ...style, marginTop: mt } : style,
  };
}

export type OldFormFieldHelperTextProps = Omit<
  OldFormHelperTextProps & JSX.IntrinsicElements[Extract<OldFormHelperTextProps['as'], string>],
  'children' | 'status'
>;

export interface OldFormFieldProps extends Omit<FormFieldNewProps, 'tooltip' | 'helperText' | 'helperTextProps'> {
  /** @deprecated tooltip을 대신 사용해주세요. */
  helper?: OldHelpTextProps;
  /** @deprecated helperText를 대신 사용해주세요. */
  formHelperText?: React.ReactNode;
  /** @deprecated helperTextProps를 대신 사용해주세요. */
  formHelperTextProps?: OldFormFieldHelperTextProps;
  /** @deprecated FormField는 항상 상단 정렬합니다. */
  alignment?: 'top' | 'center';
}

function convertOldFormFieldHelperTextProps(props?: OldFormFieldHelperTextProps): FormFieldHelperTextProps | undefined {
  return props ? convertOldFormHelperTextProps(props) : undefined;
}

export function convertOldFormFieldProps(props: OldFormFieldProps): FormFieldNewProps {
  const { helper, formHelperText, formHelperTextProps, alignment: _alignment, ...restProps } = props;

  return {
    ...restProps,
    tooltip: helper ? convertOldHelpTextProps(helper) : undefined,
    helperText: formHelperText,
    helperTextProps: convertOldFormFieldHelperTextProps(formHelperTextProps),
  };
}

const statusPropComponents = new Set<unknown>([Input, DropdownInput, NumericInput]);

export function convertOldFormFieldChildren(children: ReactNode, status?: InputStatus): ReactNode {
  if (!status || !isValidElement(children) || !statusPropComponents.has(children.type)) {
    return children;
  }

  return cloneElement(children as ReactElement<{ status?: InputStatus }>, { status });
}

export interface OldFormLabelProps extends Omit<FormLabelNewProps, 'tooltip'> {
  /** @deprecated tooltip을 대신 사용해주세요. */
  helper?: OldHelpTextProps;
}

export function convertOldFormLabelProps(props: OldFormLabelProps): FormLabelNewProps {
  const { helper, ...restProps } = props;

  return {
    ...restProps,
    tooltip: helper ? convertOldHelpTextProps(helper) : undefined,
  };
}
